/* import __COLOCATED_TEMPLATE__ from './update-attribute-operation.hbs'; */
/* RESPONSIBLE TEAM: team-workflows */

import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { type AttributeInfoResolver } from '@intercom/composer-ember';
import { tracked } from '@glimmer/tracking';
import { type OutputParam } from 'embercom/components/workflows/graph-editor/node-items/steps/fin-custom';
import type AttributeDescriptor from 'embercom/models/operator/visual-builder/attribute-descriptor';
import { EMBERCOM_PROSEMIRROR_COMPOSER_ALLOW_MOUSEDOWN_CLASS } from '@intercom/composer-ember/components/editor-api/constants';
import { inject as service } from '@ember/service';
import { type Task } from 'ember-concurrency';
import type Composer from '@intercom/composer-ember/lib/composer';
import type { NodeSelection } from 'prosemirror-state';
import type IntlService from 'embercom/services/intl';

interface Args {
  resolver: AttributeInfoResolver;
  onUpdateNode: (state: Record<string, unknown>, selection?: NodeSelection) => void;
  onCreateAttribute: (attribute: OutputParam) => Promise<AttributeDescriptor>;
  closePopover: () => void;
  composer: Composer;
}

export default class UpdateAttributeOperation extends Component<Args> {
  @tracked showAttributeCreationModal = false;
  @tracked showAttributeTypePickerModal = false;
  @tracked selectedAttributeType?: string;
  @tracked showEditAttributeModal = false;
  @tracked selectedTemporaryAttribute?: OutputParam;

  @service declare store: any;
  @service declare modalService: any;
  @service declare appService: AppService;
  @service declare intl: IntlService;
  readonly classNameToPreventComposerStealingFocus =
    EMBERCOM_PROSEMIRROR_COMPOSER_ALLOW_MOUSEDOWN_CLASS;

  get app() {
    return this.appService.app;
  }

  get availableTypes() {
    return [
      {
        text: this.intl.t(
          'operator.workflows.visual-builder.fin-custom-settings.results.attribute-descriptor-formats.string',
        ),
        icon: 'field',
        value: 'string',
        component: 'workflows/graph-editor/node-items/steps/fin-custom/data-type-select-item',
      },
      {
        text: this.intl.t(
          'operator.workflows.visual-builder.fin-custom-settings.results.attribute-descriptor-formats.integer',
        ),
        icon: 'numbers',
        value: 'fixnum',
        component: 'workflows/graph-editor/node-items/steps/fin-custom/data-type-select-item',
      },
      {
        text: this.intl.t(
          'operator.workflows.visual-builder.fin-custom-settings.results.attribute-descriptor-formats.decimal',
        ),
        icon: 'decimal',
        value: 'float',
        component: 'workflows/graph-editor/node-items/steps/fin-custom/data-type-select-item',
      },
      {
        text: this.intl.t(
          'operator.workflows.visual-builder.fin-custom-settings.results.attribute-descriptor-formats.boolean',
        ),
        icon: 'boolean',
        value: 'boolean',
        component: 'workflows/graph-editor/node-items/steps/fin-custom/data-type-select-item',
      },
      {
        text: this.intl.t(
          'operator.workflows.visual-builder.fin-custom-settings.results.attribute-descriptor-formats.date',
        ),
        icon: 'calendar',
        value: 'date',
        component: 'workflows/graph-editor/node-items/steps/fin-custom/data-type-select-item',
      },
      {
        text: this.intl.t(
          'operator.workflows.visual-builder.fin-custom-settings.results.attribute-descriptor-formats.list',
        ),
        icon: 'list',
        value: 'options',
        component: 'workflows/graph-editor/node-items/steps/fin-custom/data-type-select-item',
      },
    ];
  }

  get hasSubjectIdentifier(): boolean {
    return (
      (this.args.composer.state.prosemirrorState.selection as NodeSelection).node.attrs
        .subjectIdentifier !== undefined &&
      (this.args.composer.state.prosemirrorState.selection as NodeSelection).node.attrs
        .subjectIdentifier !== ''
    );
  }

  @action
  openModal(selectedTemporaryAttribute?: OutputParam): void {
    this.selectedTemporaryAttribute = undefined;
    if (this.app.canUseNewDataExperience) {
      if (this.hasSubjectIdentifier) {
        this.selectedTemporaryAttribute = selectedTemporaryAttribute;
        this.showEditAttributeModal = true;
        this.selectedAttributeType = 'temporary';
      } else {
        this.showAttributeTypePickerModal = true;
      }
    } else {
      // Open the temporary attribute creation modal when the app does not support agentic workflows
      this.showAttributeCreationModal = true;
      this.selectedAttributeType = 'temporary';
    }
  }

  @action
  onUpdateAttributeIdentifier(identifier: string, selection?: NodeSelection): void {
    this.args.onUpdateNode(
      {
        subjectIdentifier: identifier,
        attributeValueType: 'fin',
      },
      selection,
    );

    this.args.closePopover();
  }

  @action
  async handleOnSubmit(attribute: OutputParam): Promise<void> {
    let attr: $TSFixMe;
    let selection: NodeSelection | undefined;

    selection = this.args.composer.state.prosemirrorState.selection as NodeSelection;

    attr = await this.args.onCreateAttribute(attribute);

    this.onUpdateAttributeIdentifier(attr.identifier, selection);
  }

  @action
  openAttributeModal(type: string): void {
    this.showAttributeCreationModal = true;
    this.selectedAttributeType = type;
    this.showAttributeTypePickerModal = false;
  }

  @action
  openAttributeDetailsModal(type: string): void {
    this.modalService.openModal('settings/modals/attribute-details', {
      allowArchival: false,
      typeToCreate: type,
      showPeopleAndCompanyToggleTab: false,
      onSave: this.attributeCreated,
      useOverComposer: true,
    });
  }

  @action
  attributeCreated(attribute: $TSFixMe): void {
    this.onUpdateAttributeIdentifier(attribute.identifier);
  }

  @action
  async performTask(task: Task<any, any>): Promise<void> {
    let selection = this.args.composer.state.prosemirrorState.selection as NodeSelection;
    let attr = await task.perform();

    this.onUpdateAttributeIdentifier(attr.identifier, selection);
  }

  @action
  closePopover(): void {
    this.args.closePopover();
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Workflows::GraphEditor::NodeItems::Steps::FinCustom::AiPromptDataPopover::UpdateAttributeOperation': typeof UpdateAttributeOperation;
  }
}
